import { Col, Row } from "antd";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { INITIAL_PAGE } from "../../../../utils/helpers/constants";
import FieldBuilder from "../../../form-components/field-builder";
import Form from "../../../form-components/from";
import AisleContext from "../../../../context/wms/aisle/context";
import { AisleLayout } from "../../../../models/wms/enums";
import useHierarchicalDropdown from "../../../../hooks/wms/use-hierarchical-dropdown";
import FilterCascadeReset from "../../shared/filter-cascade-reset";

const AisleFilter: React.FC = () => {
  const { t } = useTranslation();
  const { actions, query } = useContext(AisleContext);

  // Use hierarchical dropdown hook with progressive visibility
  const {
    stores,
    loadingStores,
    zoneTypes,
    loadingZoneTypes,
    zones,
    loadingZones,
    selectedStoreId,
    selectedZoneTypeId,
    selectedZoneId,
    selectedSidesFilter,
    visibleLevels,
    setSelectedStoreId,
    setSelectedZoneTypeId,
    setSelectedZoneId,
    setSelectedSidesFilter,
  } = useHierarchicalDropdown({ debounceMs: 300 });

  return (
    <Form
      formId="aisle-filter"
      onSubmit={(data) => {
        const filter: any = {
          page: INITIAL_PAGE,
          perPage: query?.perPage,
          keyword: query?.keyword,
          filters: [
            selectedStoreId
              ? { name: "storeId", value: selectedStoreId, operation: "eq" }
              : undefined,
            selectedZoneId
              ? { name: "zoneId", value: selectedZoneId, operation: "eq" }
              : undefined,
            selectedSidesFilter
              ? { name: "sides", value: selectedSidesFilter, operation: "eq" }
              : undefined,
            data?.isActive !== undefined && data?.isActive !== null
              ? { name: "isActive", value: data.isActive, operation: "eq" }
              : undefined,
          ].filter((f) => f),
        };
        actions.setQuery(filter);
      }}
    >
      <FilterCascadeReset
        rules={[
          {
            watch: selectedStoreId,
            clear: ["zoneId", "zoneTypeId"],
            resetSetters: [setSelectedZoneId, setSelectedZoneTypeId],
          },
        ]}
      />
      <Row justify="start" gutter={[10, 0]}>
        <Col xs={24} sm={12} md={8}>
          <FieldBuilder
            name="storeId"
            label={t("select_store")}
            width="large"
            rules={{ required: false }}
            input={{
              type: "select",
              loading: loadingStores,
              allowSearch: true,
              options: stores.map((s) => ({
                label: s.label,
                value: s.id,
              })),
              onChange: setSelectedStoreId,
            }}
          />
        </Col>
        {visibleLevels.zone && (
          <Col xs={24} sm={12} md={8}>
            <FieldBuilder
              name="zoneTypeId"
              label={t("zone_type")}
              width="large"
              rules={{ required: false }}
              input={{
                type: "select",
                loading: loadingZoneTypes,
                allowSearch: true,
                options: zoneTypes.map((zt) => ({
                  label: zt.label,
                  value: zt.id,
                })),
                onChange: setSelectedZoneTypeId,
              }}
            />
          </Col>
        )}
        {visibleLevels.zone && (
          <Col xs={24} sm={12} md={8}>
            <FieldBuilder
              name="zoneId"
              label={t("select_zone")}
              width="large"
              rules={{ required: false }}
              input={{
                type: "select",
                loading: loadingZones,
                allowSearch: true,
                options: zones.map((z) => ({
                  label: z.label,
                  value: z.id,
                })),
                onChange: setSelectedZoneId,
              }}
            />
          </Col>
        )}
        <Col xs={24} sm={12} md={8}>
          <FieldBuilder
            name="sides"
            label={t("available_sides")}
            width="large"
            rules={{ required: false }}
            input={{
              type: "select",
              options: [
                { label: t("left_side"), value: AisleLayout.LEFT },
                { label: t("right_side"), value: AisleLayout.RIGHT },
                { label: t("both_sides"), value: AisleLayout.BOTH },
              ],
              onChange: setSelectedSidesFilter,
            }}
          />
        </Col>
        <Col xs={24} sm={12} md={8}>
          <FieldBuilder
            name="isActive"
            label={t("is_active")}
            width="large"
            rules={{ required: false }}
            input={{
              type: "select",
              options: [
                { label: t("active"), value: 1 },
                { label: t("inactive"), value: 0 },
              ],
            }}
          />
        </Col>
      </Row>
    </Form>
  );
};

export default AisleFilter;
